import { DownloadOutlined } from "@ant-design/icons";
import { Button, ButtonProps, Modal, Radio, Spin } from "antd";
import {usePaldockTranslation} from "hooks/usePaldockTranslation";
import React, { PropsWithChildren, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilState } from "recoil";
import { ExportExtensionEnum } from "types/enums";
import { TableId } from "types/models";

import { ExportDelimiter, ExportRange, exportAtom } from "../Table/atoms/exportAtom";

const rangeOptions: { value: ExportRange; label: string }[] = [
    { value: "all", label: "range.all_available" },
    { value: "visible", label: "range.only_displayed" },
];

interface DownloadButtonProps extends PropsWithChildren<ButtonProps> {
    /** Your export function. MUST forward the AbortSignal to the underlying request (fetch/axios) to truly cancel the request. */
    onExport?: (signal?: AbortSignal) => Promise<string | undefined>;
    withText?: boolean;
    customText?: string;
    defaultFormat?: ExportExtensionEnum;
    tableId: TableId;
    icon?: React.ReactNode;
}

// TODO! its table specific export - move to table ui
export const DownloadButtonWithModal: React.FC<DownloadButtonProps> = ({
    onExport,
    withText = true,
    customText,
    tableId,
    children,
    icon,
    ...props
}) => {
    const { t: tC } = usePaldockTranslation("common");
    const { t } = usePaldockTranslation("tables.ui.download-button-with-modal");
    const [exportState, setExportState] = useRecoilState(exportAtom(tableId));
    const [open, setOpen] = useState(false);
    const [delimiter, setDelimiter] = useState<ExportDelimiter>(ExportDelimiter.COMMA);
    const [loading, setLoading] = useState(false);

    const abortControllerRef = useRef<AbortController | null>(null);
    const suppressToastsRef = useRef(false);
    const runIdRef = useRef(0);
    const canceledRunsRef = useRef<Set<number>>(new Set());
    const unmountedRef = useRef(false);

    useEffect(() => {
        return () => {
            unmountedRef.current = true;
            abortControllerRef.current?.abort?.();
        };
    }, []);

    const format = exportState.extension || ExportExtensionEnum.CSV;

    const openModal = () => setOpen(true);
    const closeModalOnly = () => setOpen(false);

    const handleCancel = () => {
        if (loading && abortControllerRef.current) {
            const currentRunId = runIdRef.current;
            canceledRunsRef.current.add(currentRunId);
            suppressToastsRef.current = true;
            abortControllerRef.current.abort?.();
            setOpen(false);
            setLoading(false);
        } else {
            closeModalOnly();
        }
    };

    const handleExport = async () => {
        setLoading(true);
        suppressToastsRef.current = false;
        const controller = new AbortController();
        abortControllerRef.current = controller;
        const myRunId = ++runIdRef.current;
        canceledRunsRef.current.delete(myRunId);

        try {
            const downloadUrl = await onExport?.(controller.signal);

            if (canceledRunsRef.current.has(myRunId) || suppressToastsRef.current || controller.signal.aborted) {
                return;
            }

            if (!downloadUrl) {
                throw new Error("No URL");
            }

            toast.success(
                <>
                    {t("messages.export_generated_successfully")} –{" "}
                    <a
                        href={downloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cursor-pointer underline"
                        download
                    >
                        {tC("download")}
                    </a>
                </>
            );
            setOpen(false);
        } catch (error: any) {
            if (
                canceledRunsRef.current.has(myRunId) ||
                suppressToastsRef.current ||
                error?.name === "AbortError" ||
                error?.message === "canceled"
            ) {
                return;
            }
            console.error("Export error", error);
            toast.error(t("messages.export_generation_error"));
        } finally {
            abortControllerRef.current = null;
            if (!unmountedRef.current) {
                setLoading(false);
            }
            canceledRunsRef.current.delete(myRunId);
            suppressToastsRef.current = false;
        }
    };

    const buttonContent = withText
        ? customText
            ? `${customText} ${format.toUpperCase()}`
            : t("download_as", { format: format.toUpperCase() })
        : icon || children || <DownloadOutlined />;

    const formatOptions = [
        { value: ExportExtensionEnum.CSV, label: "CSV" },
        { value: ExportExtensionEnum.XLSX, label: "XLS/XLSX" },
        { value: ExportExtensionEnum.PDF, label: "PDF" },
    ];

    const delimiterOptions = [
        { value: ExportDelimiter.COMMA, label: "separator.comma" },
        { value: ExportDelimiter.SEMICOLON, label: "separator.semicolon" },
    ];

    return (
        <>
            <Button onClick={openModal} {...props} className={`!text-black ${props.className || ''}`}>
                {buttonContent}
            </Button>

            <Modal
                title={tC("download")}
                open={open}
                onCancel={handleCancel}
                maskClosable
                keyboard
                footer={[
                    <Button key="cancel" onClick={handleCancel}>
                        {tC("cancel")}
                    </Button>,
                    <Button key="export" type="primary" onClick={handleExport} disabled={loading}>
                        {loading ? (
                            <>
                                <Spin size="small" /> {t("messages.generating")}
                            </>
                        ) : (
                            tC("download")
                        )}
                    </Button>,
                ]}
            >
                <div className="flex flex-col gap-6">
                    <div>
                        <p className="mb-2 text-sm font-medium">{t("file_format")}</p>
                        <Radio.Group
                            value={format}
                            onChange={(e) =>
                                setExportState((prev) => ({
                                    ...prev,
                                    extension: e.target.value,
                                }))
                            }
                            className="flex border rounded overflow-hidden"
                        >
                            {formatOptions.map((opt) => (
                                <Radio.Button
                                    key={opt.value}
                                    value={opt.value}
                                    className={`flex-1 text-center px-4 py-2 text-sm font-medium transition-colors border-r last:border-r-0
                  ${format === opt.value ? "bg-teal-600 text-white" : "bg-white text-gray-800 hover:bg-gray-100"}`}
                                >
                                    {(opt.label)}
                                </Radio.Button>
                            ))}
                        </Radio.Group>
                    </div>

                    {(format === ExportExtensionEnum.CSV || format === ExportExtensionEnum.TSQ) && (
                        <div>
                            <p className="mb-2 text-sm font-medium">{t("separator.title")}</p>
                            <Radio.Group
                                value={delimiter}
                                onChange={(e) => {
                                    const newDel = e.target.value as ExportDelimiter;
                                    setDelimiter(newDel);
                                    setExportState((prev) => ({
                                        ...prev,
                                        extension:
                                            newDel === ExportDelimiter.COMMA
                                                ? ExportExtensionEnum.CSV
                                                : ExportExtensionEnum.TSQ,
                                        delimiter: newDel,
                                    }));
                                }}
                                className="flex border rounded overflow-hidden"
                            >
                                {delimiterOptions.map((opt) => (
                                    <Radio.Button
                                        key={opt.value}
                                        value={opt.value}
                                        className={`flex-1 text-center px-4 py-2 text-sm font-medium transition-colors border-r last:border-r-0
                    ${delimiter === opt.value ? "bg-teal-600 text-white" : "bg-white text-gray-800 hover:bg-gray-100"}`}
                                    >
                                        {t(opt.label)}
                                    </Radio.Button>
                                ))}
                            </Radio.Group>
                        </div>
                    )}

                    <div>
                        <p className="mb-2 text-sm font-medium">{t("range.title")}</p>
                        <Radio.Group
                            value={exportState.range}
                            onChange={(e) => setExportState((prev) => ({ ...prev, range: e.target.value }))}
                            className="flex border rounded overflow-hidden"
                        >
                            {rangeOptions.map((opt) => (
                                <Radio.Button
                                    key={opt.value}
                                    value={opt.value}
                                    className="flex-1 text-center px-4 py-2 text-sm font-medium border-r last:border-r-0"
                                >
                                    {t(opt.label)}
                                </Radio.Button>
                            ))}
                        </Radio.Group>
                    </div>
                </div>
            </Modal>
        </>
    );
};
